
import { useState, useRef, useCallback, useEffect } from 'react';
import { useDeepgram } from '@/hooks/useDeepgram';
import { DEEPGRAM_API_KEY } from '@/config/api';
import { ApiTranscriptionData } from '@/types/sections';
import { updateAnalysisTranscription } from '@/config/api';
import { debugLog, debugError, debugWarn } from '@/config/debug';

interface UseConsultationTranscriptionProps {
  analysisId?: number;
  isRecording: boolean;
  onTranscriptionUpdate?: (transcriptions: ApiTranscriptionData[]) => void;
  isContinuation?: boolean;
  currentTime?: number;
  onMicrophoneNotFound?: () => void;
  onMicrophoneVerified?: () => Promise<number | null>;
  shouldCreateAnalysis?: boolean;
}

// Función robusta de deduplicación
const deduplicateTranscriptions = (transcriptions: ApiTranscriptionData[]): ApiTranscriptionData[] => {
  debugLog('DEDUP', '🔍 DEDUP: Starting deduplication process with', transcriptions.length, 'transcriptions');
  
  const uniqueTranscriptions: ApiTranscriptionData[] = [];
  const seenKeys = new Set<string>();
  
  transcriptions.forEach((transcription, index) => {
    // Crear clave única basada en timestamp, speaker y contenido
    const textPrefix = transcription.transcript.substring(0, 30).replace(/\s+/g, '_');
    const uniqueKey = `${transcription.recordTime}-${transcription.speakerId}-${textPrefix}`;
    
    if (!seenKeys.has(uniqueKey)) {
      seenKeys.add(uniqueKey);
      uniqueTranscriptions.push(transcription);
      debugLog('DEDUP', `✅ DEDUP: Keeping transcription ${index + 1}: "${transcription.recordTime}" - "${transcription.transcript.substring(0, 30)}..."`);
    } else {
      debugLog('DEDUP', `❌ DEDUP: REMOVING DUPLICATE ${index + 1}: "${transcription.recordTime}" - "${transcription.transcript.substring(0, 30)}..."`);
    }
  });
  
  debugLog('DEDUP', '🎯 DEDUP: Deduplication complete. Before:', transcriptions.length, 'After:', uniqueTranscriptions.length);
  return uniqueTranscriptions;
};

export const useConsultationTranscription = ({
  analysisId,
  isRecording,
  onTranscriptionUpdate,
  isContinuation = false,
  currentTime = 0,
  onMicrophoneNotFound,
  onMicrophoneVerified,
  shouldCreateAnalysis = false
}: UseConsultationTranscriptionProps) => {
  const [transcriptions, setTranscriptions] = useState<ApiTranscriptionData[]>([]);
  
  // Refs for stable values
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastAnalysisIdRef = useRef<number | undefined>(undefined);
  const isMountedRef = useRef(true);
  const isRecordingRef = useRef(false);
  const lastSaveRef = useRef<ApiTranscriptionData[]>([]);
  const isContinuationRef = useRef(isContinuation);
  const hasInitializedRef = useRef(false);
  const persistentTranscriptionsRef = useRef<ApiTranscriptionData[]>([]);
  const hasLoadedExistingRef = useRef(false);
  
  // Una sola fuente de verdad para todas las transcripciones
  const allTranscriptionsRef = useRef<ApiTranscriptionData[]>([]);
  
  // Sistema robusto de tiempo con múltiples fuentes
  const realTimeRef = useRef<number>(0);
  const sessionStartTimeRef = useRef<number | null>(null);
  const lastValidTimeRef = useRef<number>(0);
  
  // Stable callback refs
  const onTranscriptionUpdateRef = useRef(onTranscriptionUpdate);
  const analysisIdRef = useRef(analysisId);
  const onMicrophoneVerifiedRef = useRef(onMicrophoneVerified);
  const shouldCreateAnalysisRef = useRef(shouldCreateAnalysis);

  // Update refs when props change
  useEffect(() => {
    onTranscriptionUpdateRef.current = onTranscriptionUpdate;
  }, [onTranscriptionUpdate]);

  useEffect(() => {
    analysisIdRef.current = analysisId;
  }, [analysisId]);

  useEffect(() => {
    onMicrophoneVerifiedRef.current = onMicrophoneVerified;
  }, [onMicrophoneVerified]);

  useEffect(() => {
    shouldCreateAnalysisRef.current = shouldCreateAnalysis;
    debugLog('TRANSCRIPTION', '🔧 TRANSCRIPT: shouldCreateAnalysis updated:', shouldCreateAnalysis);
  }, [shouldCreateAnalysis]);

  useEffect(() => {
    isRecordingRef.current = isRecording;
    
    // CRÍTICO: Cuando inicia la grabación, establecer tiempo de sesión
    if (isRecording && !sessionStartTimeRef.current) {
      sessionStartTimeRef.current = Date.now();
      realTimeRef.current = currentTime;
      debugLog('TIMESTAMP', '🕐 TIMESTAMP: Recording started, session time initialized:', {
        sessionStart: sessionStartTimeRef.current,
        initialTime: currentTime,
        realTimeRefValue: realTimeRef.current
      });
    }
    
    // Cuando para la grabación, mantener el tiempo final
    if (!isRecording && sessionStartTimeRef.current) {
      debugLog('TIMESTAMP', '🕐 TIMESTAMP: Recording stopped, preserving final time:', realTimeRef.current);
    }
  }, [isRecording, currentTime]);

  // Actualizar tiempo real de manera confiable con logs detallados
  useEffect(() => {
    const previousTime = realTimeRef.current;
    
    debugLog('TIMESTAMP', '🕐 TIMESTAMP DEBUG: Time update effect triggered:', {
      currentTime,
      previousTime,
      isRecording,
      sessionActive: !!sessionStartTimeRef.current,
      timeIsValid: currentTime > 0
    });
    
    // Solo actualizar si es un tiempo válido (> 0)
    if (currentTime > 0) {
      realTimeRef.current = currentTime;
      lastValidTimeRef.current = currentTime;
      
      debugLog('TIMESTAMP', '🕐 TIMESTAMP: Time updated reliably -', {
        previousTime,
        newTime: currentTime,
        isRecording,
        sessionActive: !!sessionStartTimeRef.current
      });
    } else {
      debugWarn('TIMESTAMP', '⚠️ TIMESTAMP: Received invalid currentTime (0 or negative):', currentTime);
    }
  }, [currentTime, isRecording]);

  // Función para formatear el tiempo
  const formatTime = useCallback((seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const formatted = `${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    
    debugLog('TIMESTAMP', '🕐 FORMAT TIME:', {
      inputSeconds: seconds,
      calculatedMinutes: minutes,
      calculatedSeconds: secs,
      formattedResult: formatted
    });
    
    return formatted;
  }, []);

  // Función ultra-robusta para obtener timestamp válido SOLO para transcripciones NUEVAS
  const getReliableTimestamp = useCallback(() => {
    debugLog('TIMESTAMP', '🕐 TIMESTAMP: Getting reliable timestamp for NEW transcription...');
    
    // Opción 1: Usar tiempo real actualizado
    if (realTimeRef.current > 0) {
      const timestamp = {
        seconds: realTimeRef.current,
        formatted: formatTime(realTimeRef.current)
      };
      debugLog('TIMESTAMP', '🕐 TIMESTAMP: Using realTimeRef (OPTION 1):', timestamp);
      return timestamp;
    }
    
    // Opción 2: Calcular desde el inicio de sesión
    if (sessionStartTimeRef.current && isRecordingRef.current) {
      const elapsedMs = Date.now() - sessionStartTimeRef.current;
      const elapsedSeconds = Math.floor(elapsedMs / 1000);
      const calculatedTime = Math.max(1, elapsedSeconds);
      
      const timestamp = {
        seconds: calculatedTime,
        formatted: formatTime(calculatedTime)
      };
      debugLog('TIMESTAMP', '🕐 TIMESTAMP: Using calculated session time (OPTION 2):', timestamp);
      return timestamp;
    }
    
    // Opción 3: Usar último tiempo válido conocido
    if (lastValidTimeRef.current > 0) {
      const timestamp = {
        seconds: lastValidTimeRef.current,
        formatted: formatTime(lastValidTimeRef.current)
      };
      debugLog('TIMESTAMP', '🕐 TIMESTAMP: Using last valid time (OPTION 3):', timestamp);
      return timestamp;
    }
    
    // Opción 4: Fallback mínimo
    debugError('TIMESTAMP', '⚠️ TIMESTAMP: All time sources failed, using minimal fallback');
    
    return {
      seconds: 1,
      formatted: "00:01"
    };
  }, [formatTime, currentTime]);

  // Función robusta para actualizar transcripciones con deduplicación
  const updateTranscriptionsWithDeduplication = useCallback((newTranscriptions: ApiTranscriptionData[]) => {
    if (!isMountedRef.current) return;

    debugLog('TRANSCRIPTION', '🔄 TRANSCRIPT: Updating transcriptions with deduplication');
    debugLog('TRANSCRIPTION', '📊 TRANSCRIPT: Before deduplication - Input:', newTranscriptions.length, 'Current:', allTranscriptionsRef.current.length);
    
    // Aplicar deduplicación robusta
    const deduplicatedTranscriptions = deduplicateTranscriptions(newTranscriptions);
    
    debugLog('TRANSCRIPTION', '📊 TRANSCRIPT: After deduplication:', deduplicatedTranscriptions.length);
    
    // Actualizar ref y estado
    allTranscriptionsRef.current = deduplicatedTranscriptions;
    setTranscriptions(deduplicatedTranscriptions);
    persistentTranscriptionsRef.current = deduplicatedTranscriptions;
    
    // Notificar a parent component
    if (isMountedRef.current) {
      onTranscriptionUpdateRef.current?.(deduplicatedTranscriptions);
    }
    
    debugLog('TRANSCRIPTION', '✅ TRANSCRIPT: Transcriptions updated with deduplication complete');
  }, []);

  // Función para guardar transcripciones
  const saveTranscriptions = useCallback(async (transcriptionsToSave: ApiTranscriptionData[], immediate = false) => {
    const currentAnalysisId = analysisIdRef.current;
    if (!currentAnalysisId || !isMountedRef.current) {
      debugLog('SAVE', '❌ SAVE: Cannot save - no analysis ID or not mounted');
      return;
    }

    // Filtrar solo transcripciones que NO vienen del servidor
    const newTranscriptionsOnly = transcriptionsToSave.filter(t => !t.isFromServer);
    
    if (newTranscriptionsOnly.length === 0) {
      debugLog('SAVE', '📝 SAVE: No new transcriptions to save (all are from server)');
      return;
    }

    debugLog('SAVE', '💾 SAVE: Saving transcriptions:', newTranscriptionsOnly.length, 'of', transcriptionsToSave.length, 'total');

    if (immediate) {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
      
      try {
        await updateAnalysisTranscription(currentAnalysisId, transcriptionsToSave);
        lastSaveRef.current = [...transcriptionsToSave];
        debugLog('SAVE', '✅ SAVE: Transcriptions saved immediately to server');
      } catch (error) {
        debugError('SAVE', '❌ SAVE: Error saving transcriptions immediately:', error);
      }
      return;
    }

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(async () => {
      if (!isMountedRef.current) return;
      
      try {
        await updateAnalysisTranscription(currentAnalysisId, transcriptionsToSave);
        lastSaveRef.current = [...transcriptionsToSave];
        debugLog('SAVE', '✅ SAVE: Transcriptions saved to server');
      } catch (error) {
        debugError('SAVE', '❌ SAVE: Error saving transcriptions:', error);
      }
    }, 1000);
  }, []);

  // Handler para transcripciones NUEVAS de Deepgram
  const handleNewDeepgramTranscription = useCallback((transcription: any) => {
    if (!isMountedRef.current) {
      debugLog('TRANSCRIPTION', '❌ TRANSCRIPT: Component not mounted, skipping transcription');
      return;
    }
    
    debugLog('TRANSCRIPTION', '🎤 TRANSCRIPT: NEW DEEPGRAM transcription received:', transcription);
    
    if (!transcription.text || typeof transcription.text !== 'string') {
      debugLog('TRANSCRIPTION', '❌ TRANSCRIPT: Invalid transcription - no text');
      return;
    }

    const cleanText = transcription.text.trim();
    if (cleanText.length === 0) {
      debugLog('TRANSCRIPTION', '❌ TRANSCRIPT: Empty transcription text');
      return;
    }

    // Obtener timestamp confiable
    debugLog('TRANSCRIPTION', '🕐 TRANSCRIPT: Getting reliable timestamp for NEW transcription...');
    const reliableTimestamp = getReliableTimestamp();
    debugLog('TRANSCRIPTION', '🕐 TRANSCRIPT: Got reliable timestamp:', reliableTimestamp);
    
    let speakerType = "Hablante";
    if (transcription.speaker !== undefined && transcription.speaker !== null) {
      speakerType = `Hablante ${transcription.speaker}`;
    }

    const newTranscription: ApiTranscriptionData = {
      speakerId: transcription.speaker?.toString() || "0",
      transcript: cleanText,
      recordTime: reliableTimestamp.formatted,
      speakerType: speakerType,
      isFromServer: false
    };

    debugLog('TRANSCRIPTION', '📝 TRANSCRIPT: CREATING NEW transcription:', {
      recordTime: newTranscription.recordTime,
      transcript: newTranscription.transcript.substring(0, 50) + '...',
      speakerType: newTranscription.speakerType,
      isFromServer: newTranscription.isFromServer,
      source: 'DEEPGRAM_NEW'
    });

    // CRÍTICO: Combinar con existentes y aplicar deduplicación
    const combinedTranscriptions = [...allTranscriptionsRef.current, newTranscription];
    updateTranscriptionsWithDeduplication(combinedTranscriptions);
    
    // Guardar si estamos grabando
    if (isRecordingRef.current) {
      debugLog('TRANSCRIPTION', '💾 TRANSCRIPT: Triggering save for NEW transcription');
      saveTranscriptions(combinedTranscriptions);
    }
  }, [getReliableTimestamp, updateTranscriptionsWithDeduplication, saveTranscriptions]);

  // Función para cargar transcripciones existentes
  const loadExistingTranscriptions = useCallback((existingTranscriptions: ApiTranscriptionData[]) => {
    debugLog('TRANSCRIPTION', '📥 TRANSCRIPT: Loading existing transcriptions from server:', existingTranscriptions.length);
    debugLog('TRANSCRIPTION', '📥 TRANSCRIPT: hasLoadedExisting:', hasLoadedExistingRef.current);
    
    // CRÍTICO: Solo cargar UNA VEZ para evitar duplicados
    if (hasLoadedExistingRef.current) {
      debugLog('TRANSCRIPTION', '⚠️ TRANSCRIPT: Existing transcriptions already loaded, skipping to prevent duplicates');
      return;
    }

    // Marcar todas como del servidor y proteger sus timestamps
    const protectedTranscriptions = existingTranscriptions.map((t, index) => {
      debugLog('TRANSCRIPTION', `📥 TRANSCRIPT: Existing ${index + 1} - PROTECTING timestamp: "${t.recordTime}" - text: "${t.transcript.substring(0, 30)}..."`);
      
      return {
        ...t,
        isFromServer: true // CRÍTICO: Marcar como del servidor para proteger
      };
    });

    // Aplicar deduplicación y actualizar
    updateTranscriptionsWithDeduplication(protectedTranscriptions);
    hasLoadedExistingRef.current = true;
    
    debugLog('TRANSCRIPTION', '✅ TRANSCRIPT: Existing transcriptions loaded and PROTECTED from re-processing');
  }, [updateTranscriptionsWithDeduplication]);

  // Handler for successful microphone verification
  const handleMicrophoneVerified = useCallback(async (): Promise<number | null> => {
    debugLog('HOOK', '🎤 TRANSCRIPT: Microphone verified successfully');
    debugLog('HOOK', '🎤 TRANSCRIPT: shouldCreateAnalysis:', shouldCreateAnalysisRef.current);
    
    if (shouldCreateAnalysisRef.current && onMicrophoneVerifiedRef.current) {
      debugLog('HOOK', '🎤 TRANSCRIPT: Creating NEW analysis because shouldCreateAnalysis=true');
      const newAnalysisId = await onMicrophoneVerifiedRef.current();
      if (newAnalysisId) {
        analysisIdRef.current = newAnalysisId;
        debugLog('HOOK', '🎤 TRANSCRIPT: Analysis created and updated to:', newAnalysisId);
        return newAnalysisId;
      }
    } else {
      debugLog('HOOK', '🎤 TRANSCRIPT: NOT creating analysis because shouldCreateAnalysis=false (continuation mode)');
      debugLog('HOOK', '🎤 TRANSCRIPT: Using existing analysis ID:', analysisIdRef.current);
    }
    
    return null;
  }, []);

  // Simplified error handler
  const handleError = useCallback((error: string) => {
    debugError('HOOK', "❌ HOOK: Deepgram Error:", error);
  }, []);

  // Configurar Deepgram con callbacks estables
  const { 
    startRecording, 
    stopRecording, 
    isRecording: deepgramIsRecording,
    connectionStatus 
  } = useDeepgram({
    apiKey: DEEPGRAM_API_KEY,
    onTranscriptionReceived: handleNewDeepgramTranscription,
    onError: handleError,
    onMicrophoneNotFound: onMicrophoneNotFound,
    onMicrophoneVerified: handleMicrophoneVerified
  });

  const startRecordingRef = useRef(startRecording);
  const stopRecordingRef = useRef(stopRecording);

  useEffect(() => {
    startRecordingRef.current = startRecording;
  }, [startRecording]);

  useEffect(() => {
    stopRecordingRef.current = stopRecording;
  }, [stopRecording]);

  // Control de Deepgram
  useEffect(() => {
    const currentAnalysisId = analysisIdRef.current;
    
    debugLog('HOOK', '🔄 HOOK: Recording state change:', {
      isRecording,
      deepgramIsRecording,
      currentAnalysisId,
      shouldCreateAnalysis: shouldCreateAnalysisRef.current
    });
    
    if (isRecording && !deepgramIsRecording && currentAnalysisId) {
      debugLog('HOOK', '🚀 HOOK: STARTING Deepgram transcription for analysis:', currentAnalysisId);
      if (isMountedRef.current) {
        startRecordingRef.current();
      }
    } 
    else if (!isRecording && deepgramIsRecording) {
      debugLog('HOOK', '🛑 HOOK: STOPPING Deepgram transcription - saving current transcriptions');
      
      // Guardar transcripciones actuales
      if (allTranscriptionsRef.current.length > 0 && currentAnalysisId) {
        debugLog('HOOK', '💾 HOOK: Saving', allTranscriptionsRef.current.length, 'transcriptions before stop');
        saveTranscriptions(allTranscriptionsRef.current, true);
      }
      
      if (isMountedRef.current) {
        stopRecordingRef.current();
      }
    }
  }, [isRecording, deepgramIsRecording, saveTranscriptions]);

  // Inicialización inmediata cuando es continuación
  useEffect(() => {
    debugLog('HOOK', '🔄 HOOK: Hook initialization:', { 
      isContinuation, 
      analysisId,
      hasInitialized: hasInitializedRef.current,
      shouldCreateAnalysis: shouldCreateAnalysisRef.current
    });
    
    if (isContinuation && !hasInitializedRef.current) {
      debugLog('HOOK', '🔄 HOOK: IMMEDIATE CONTINUATION: Preventing transcription loss');
      hasInitializedRef.current = true;
      isContinuationRef.current = true;
      return;
    }
    
    isContinuationRef.current = isContinuation;
  }, [isContinuation, analysisId]);

  // CRÍTICO: Solo resetear cuando NO sea continuación
  useEffect(() => {
    const currentAnalysisId = analysisIdRef.current;
    const lastAnalysisId = lastAnalysisIdRef.current;
    
    debugLog('HOOK', '🔄 HOOK: Analysis effect:', {
      currentAnalysisId,
      lastAnalysisId,
      isContinuation: isContinuationRef.current,
      hasInitialized: hasInitializedRef.current,
      shouldCreateAnalysis: shouldCreateAnalysisRef.current
    });
    
    if (currentAnalysisId && currentAnalysisId !== lastAnalysisId && hasInitializedRef.current) {
      if (!isContinuationRef.current) {
        debugLog('HOOK', '🔄 HOOK: NEW ANALYSIS - Resetting ALL transcription collections');
        setTranscriptions([]);
        allTranscriptionsRef.current = [];
        lastSaveRef.current = [];
        persistentTranscriptionsRef.current = [];
        hasLoadedExistingRef.current = false; // Reset flag para nuevo análisis
        sessionStartTimeRef.current = null;
        realTimeRef.current = 0;
        lastValidTimeRef.current = 0;
      } else {
        debugLog('HOOK', '🔄 HOOK: CONTINUING ANALYSIS - Preserving transcription collections for:', currentAnalysisId);
      }
    }
    
    if (currentAnalysisId && !hasInitializedRef.current) {
      hasInitializedRef.current = true;
      lastAnalysisIdRef.current = currentAnalysisId;
    } else if (currentAnalysisId) {
      lastAnalysisIdRef.current = currentAnalysisId;
    }
  }, [analysisId]);

  // Track component mount state
  useEffect(() => {
    isMountedRef.current = true;
    debugLog('HOOK', '🚀 HOOK: useConsultationTranscription MOUNTED for analysis:', analysisIdRef.current);
    
    return () => {
      debugLog('HOOK', '🧹 HOOK: useConsultationTranscription UNMOUNTING...');
      isMountedRef.current = false;
      
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      
      // Guardar transcripciones finales en unmount
      const currentAnalysisId = analysisIdRef.current;
      if (allTranscriptionsRef.current.length > 0 && currentAnalysisId) {
        debugLog('HOOK', '💾 HOOK: FINAL SAVE on unmount - transcriptions:', allTranscriptionsRef.current.length);
        updateAnalysisTranscription(currentAnalysisId, allTranscriptionsRef.current).catch(console.error);
      }
    };
  }, []);

  return {
    transcriptions,
    connectionStatus,
    isTranscribing: deepgramIsRecording,
    loadExistingTranscriptions
  };
};
